import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import { updateIntegration } from 'api/integrations';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import * as Form from 'components/form';
import { InputCopyButton } from 'components/input';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { useCurrentAccountSettings } from 'customHooks/useCurrentAccountSettings';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account, Integration } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type SubscriptionType = 'default' | 'mailchimp';

interface MailchimpFormValues {
    apiKey: string;
    defaultListId: string;
    type: SubscriptionType;
    callToAction: string;
}

const Mailchimp = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: integrations } = useCurrentAccountIntegrations();
    const { mailchimp: mailchimpIntegration } = integrations || {};
    const settings = useCurrentAccountSettings();
    const { customSubscription: mailchimpSettings } = settings || {};
    const hasStoredValues = !!mailchimpIntegration;
    const [isEditing, setIsEditing] = useState(false);
    const firstInputRef = useRef<HTMLInputElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const defaultValues = {
        apiKey: mailchimpIntegration?.authentication.api_key,
        defaultListId: mailchimpIntegration?.settings.default_list_id,
        type: mailchimpSettings?.value.type ?? 'default',
        callToAction: mailchimpSettings?.value.call_to_action,
    };
    const methods = Form.useGivebutterForm<MailchimpFormValues>({ defaultValues });
    const {
        control,
        formState: { isDirty, isValid },
        handleSubmit,
        path,
        watch,
        reset,
    } = methods;
    const [type] = watch(['type']);

    const { mutate: submit, isPending: isLoading } = useMutation<
        [Account, Integration],
        GivebutterAxiosError,
        MailchimpFormValues
    >({
        mutationFn: values => {
            if (!hasStoredValues)
                return Promise.reject(new Error('Mailchimp Integration not available.'));

            return Promise.all([
                updateAccount(accountId, {
                    settings: [
                        {
                            code: Settings.CustomSubscription.Code,
                            name: 'custom_subscription',
                            active: values.type !== 'default',
                            type: 'json',
                            value: {
                                type: values.type,
                                call_to_action: values.callToAction,
                            },
                        },
                    ],
                }),
                updateIntegration(accountId, mailchimpIntegration.id, {
                    api_key: values.apiKey,
                    name: 'mailchimp',
                    settings: {
                        default_list_id: values.defaultListId,
                    },
                }),
            ]);
        },
        onSuccess: ([settingsResponse, integrationResponse]) => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            success('Saved Mailchimp settings!');
            setIsEditing(false);
            const mailchimpSetting = settingsResponse.settings.find(
                item => item.code === Settings.CustomSubscription.Code,
            );
            reset({
                apiKey: integrationResponse.authentication.api_key,
                defaultListId: integrationResponse.settings.default_list_id,
                type: mailchimpSetting?.value.type,
                callToAction: mailchimpSetting?.value.call_to_action,
            });
        },
        onError: e => {
            error(e.response?.data?.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="Mailchimp"
                        className="max-w-[100px]"
                        src={`/images/logos/mailchimp.svg`}
                    />
                </IntegrationItem.Logo>

                <IntegrationItem.Description>
                    <div className="flex items-center">
                        <p className="text-xl font-medium">Mailchimp</p>
                    </div>

                    <p className="max-w-xs text-base text-gray-700">
                        Present your supporters with the option to subscribe to your newsletter.
                    </p>

                    <a target="_blank" rel="noopener noreferrer" href="https://mailchimp.com">
                        https://mailchimp.com
                    </a>
                </IntegrationItem.Description>
                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex max-w-sm flex-col items-start gap-4"
                    >
                        <Form.Container>
                            <Form.Label name={path('apiKey')} className="text-base">
                                Private API Key
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('apiKey')}
                                control={control}
                                disabled={hasStoredValues && !isEditing}
                                rules={{ required: 'Please enter your Public API Key' }}
                            >
                                <Form.TextInputForm placeholder="Enter your Public API key" />
                                {hasStoredValues && !isEditing && (
                                    <InputCopyButton
                                        text={mailchimpIntegration.authentication.api_key}
                                    />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('apiKey')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('defaultListId')} className="text-base">
                                Audience (Newsletter) ID
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('defaultListId')}
                                control={control}
                                disabled={hasStoredValues && !isEditing}
                                rules={{ required: 'Please enter your Audience (Newsletter) ID' }}
                            >
                                <Form.TextInputForm placeholder="Enter your Audience (Newsletter) ID" />
                                {hasStoredValues && !isEditing && (
                                    <InputCopyButton
                                        text={mailchimpIntegration.settings.default_list_id}
                                    />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('defaultListId')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.UseFormLabel name={path('type')}>
                                Post-Gift Newsletter
                            </Form.UseFormLabel>

                            <p className="text-sm">
                                Configure the post-gift newsletter box that appears after someone
                                makes a gift.
                            </p>

                            <Form.SingleSelect control={control} name="type">
                                <SingleSelect.Trigger
                                    disabled={hasStoredValues && !isEditing}
                                    aria-label="Type"
                                />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="default">
                                        <SingleSelect.ItemText>
                                            Default (Givebutter Newsletter)
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="mailchimp">
                                        <SingleSelect.ItemText>
                                            Use my Mailchimp List
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </Form.Container>

                        {type === 'mailchimp' ? (
                            <Form.Container>
                                <Form.Label name={path('defaultListId')} className="text-base">
                                    Call to Action
                                </Form.Label>
                                <Form.TextInputRoot
                                    name={path('callToAction')}
                                    control={control}
                                    disabled={hasStoredValues && !isEditing}
                                    rules={{
                                        required:
                                            type === 'mailchimp' &&
                                            'Please enter your Call to Action text.',
                                    }}
                                >
                                    <Form.TextInputForm placeholder="Enter your Call to Action text" />
                                </Form.TextInputRoot>
                                <Form.Error name={path('defaultListId')} />
                            </Form.Container>
                        ) : null}

                        {hasStoredValues && !isEditing ? (
                            <Button
                                variant="solid"
                                ref={editButtonRef}
                                onClick={() => {
                                    flushSync(() => {
                                        setIsEditing(true);
                                    });
                                    firstInputRef.current?.select();
                                }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <div className="flex gap-4">
                                <Form.SubmitButton
                                    isSubmitting={isLoading}
                                    disabled={!isDirty || !isValid}
                                >
                                    Save
                                </Form.SubmitButton>
                                {hasStoredValues ? (
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        onClick={() => {
                                            flushSync(() => {
                                                setIsEditing(false);
                                            });
                                            editButtonRef.current?.focus();
                                            reset(defaultValues);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                            </div>
                        )}
                    </form>
                </Form.Provider>
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { Mailchimp };
